/**
 * Block F.3: детальная страница admin для одной заявки на вывод.
 * Показывает: партнёр, сумма, метод, destination, status, timeline, авто-статус.
 * Действия (зависят от статуса):
 *   pending  → Одобрить / Отклонить / Завершить (отправлено) / Повторить авто
 *   approved → Завершить (отправлено) / Отклонить
 *   completed/rejected → только просмотр
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { PayoutDetail } from '@/api/types';
import { Card, CardContent } from '@/components/ui/card';
import { motion } from 'motion/react';
import {
  ArrowDownRight,
  Check,
  X,
  Loader2,
  User as UserIcon,
  Wallet,
  Clock,
  Send,
  RefreshCw,
  Copy,
  Zap,
  AlertTriangle,
} from 'lucide-react';
import { getWebApp, haptic } from '@/lib/telegram';
import { toast } from 'sonner';

export const Route = createFileRoute('/withdrawals/$id')({ component: WithdrawalDetail });

function WithdrawalDetail() {
  const navigate = useNavigate();
  const { id } = Route.useParams();
  const qc = useQueryClient();

  const { data, isPending } = useQuery({
    queryKey: ['payout', id],
    queryFn: () => api.payoutDetail(id),
  });

  useEffect(() => {
    const tg = getWebApp();
    tg?.BackButton.show();
    const onBack = () => navigate({ to: '/finance' });
    tg?.BackButton.onClick(onBack);
    return () => {
      tg?.BackButton.offClick(onBack);
      tg?.BackButton.hide();
    };
  }, [navigate]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['payout', id] });
    qc.invalidateQueries({ queryKey: ['payouts-pending'] });
    qc.invalidateQueries({ queryKey: ['payouts-history'] });
  };

  const { mutate: decide, isPending: deciding } = useMutation({
    mutationFn: (approve: boolean) => api.decideWithdraw(id, approve),
    onSuccess: (res) => {
      invalidate();
      haptic('success');
      toast.success(res.status === 'approved' ? 'Одобрено' : 'Отклонено');
    },
    onError: () => {
      haptic('error');
      toast.error('Не удалось');
    },
  });

  const { mutate: complete, isPending: completing } = useMutation({
    mutationFn: () => api.completePayout(id),
    onSuccess: () => {
      invalidate();
      haptic('success');
      toast.success('Завершено — деньги отправлены');
    },
    onError: () => {
      haptic('error');
      toast.error('Не удалось завершить');
    },
  });

  const { mutate: retry, isPending: retrying } = useMutation({
    mutationFn: () => api.retryPayout(id),
    onSuccess: (res) => {
      invalidate();
      if (res.ok) {
        haptic('success');
        toast.success('Авто-выплата прошла');
      } else {
        haptic('error');
        toast.error(`Авто-выплата не удалась: ${res.error ?? 'ошибка'}`);
      }
    },
    onError: () => {
      haptic('error');
      toast.error('Не удалось');
    },
  });

  if (isPending || !data) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const busy = deciding || completing || retrying;
  const isPendingStatus = data.status === 'pending';
  const isApproved = data.status === 'approved';
  // Перевод в пути или его судьба неизвестна: ручные решения бэк отбивает, отклонение
  // вернуло бы деньги партнёру, а перевод всё равно дойдёт.
  const transferLive =
    data.transfer_state === 'sending' ||
    data.transfer_state === 'sent' ||
    data.transfer_state === 'unknown' ||
    !!data.can_resolve;
  const canFinalize = (isPendingStatus || isApproved) && !transferLive;
  // Способы с автоисполнением: повтор нужен, когда авто-выплата упала до отправки
  // (мало денег на кошельке, переводы выключены). Неясный исход разбирают «Ушёл / Не ушёл».
  const isCryptoMethod = ['cryptobot', 'crypto', 'xrocket'].includes(data.method);
  const canRetryAuto = isPendingStatus && isCryptoMethod && !data.auto_executed;

  const copy = (text: string) => {
    navigator.clipboard?.writeText(text);
    haptic('light');
    toast.success('Скопировано');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="p-4 space-y-4 pb-32"
    >
      <div className="px-2 pt-2 flex items-start gap-3">
        <div className="w-14 h-14 rounded-2xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
          <ArrowDownRight className="w-7 h-7" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-bold tracking-tight">
            ${(data.amount_usd_minor / 100).toFixed(2)}
          </h2>
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            {data.method.toUpperCase()} · #{data.id.slice(0, 8)}
            {data.auto_executed && (
              <span className="inline-flex items-center gap-0.5 text-blue-400">
                <Zap className="w-3 h-3" /> авто
              </span>
            )}
          </p>
        </div>
        <StatusBadge status={data.status} />
      </div>

      {data.auto_error && !data.completed_at && (
        <div className="flex items-start gap-2 rounded-xl bg-yellow-500/10 border border-yellow-500/20 p-3 text-xs text-yellow-500">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-bold">Ошибка авто-выплаты</div>
            <div className="opacity-90 break-words">{data.auto_error}</div>
          </div>
        </div>
      )}

      {transferLive && !data.completed_at && (
        <div className="flex items-start gap-2 rounded-xl bg-blue-500/10 border border-blue-500/20 p-3 text-xs text-blue-400">
          <Clock className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            {data.can_resolve ? (
              <>
                <div className="font-bold">Неизвестно, ушли ли деньги</div>
                <div className="opacity-90">
                  Автоматика заявку не повторяет. Отметьте результат кнопками в топике «Выплаты»
                  или в админке сайта.
                </div>
              </>
            ) : (
              <>
                <div className="font-bold">Перевод в пути</div>
                <div className="opacity-90">Заявку закроет подтверждение сети.</div>
              </>
            )}
          </div>
        </div>
      )}
      <PayAmount data={data} onCopy={copy} />

      <Card>
        <CardContent className="p-5 space-y-3">
          <Row icon={<UserIcon className="w-4 h-4" />} label="Партнёр">
            <span>{data.partner_name}</span>
            {data.partner_tg_username && (
              <span className="text-muted-foreground"> @{data.partner_tg_username}</span>
            )}
          </Row>
          <Row icon={<Wallet className="w-4 h-4" />} label="Получатель">
            <button
              type="button"
              onClick={() => copy(data.destination)}
              className="inline-flex items-center gap-1 font-mono text-xs break-all text-left"
            >
              {data.destination}
              <Copy className="w-3 h-3 shrink-0 opacity-60" />
            </button>
          </Row>
          {data.note && <Row label="Заметка" value={data.note} />}
          {data.external_ref && (
            <Row label="Внешний референс">
              <button
                type="button"
                onClick={() => copy(data.external_ref!)}
                className="inline-flex items-center gap-1 font-mono text-xs break-all text-left"
              >
                {data.external_ref}
                <Copy className="w-3 h-3 shrink-0 opacity-60" />
              </button>
            </Row>
          )}
          {data.rejection_reason && (
            <Row label="Причина отказа" value={data.rejection_reason} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5 space-y-2">
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
            <Clock className="inline w-3 h-3 mr-1" />
            Timeline
          </h3>
          <TimelineRow label="Создано" ts={data.created_at} />
          {data.processed_at && (
            <TimelineRow
              label={data.status === 'rejected' ? 'Отклонено' : 'Обработано'}
              ts={data.processed_at}
            />
          )}
          {data.completed_at && <TimelineRow label="Выплачено" ts={data.completed_at} />}
        </CardContent>
      </Card>

      {canFinalize && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => decide(false)}
              className="bg-red-500/15 text-red-500 py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <X className="w-4 h-4" />
              Отклонить
            </button>
            {isPendingStatus ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => decide(true)}
                className="bg-blue-500/15 text-blue-400 py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {deciding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Одобрить
              </button>
            ) : (
              <span />
            )}
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={() => complete()}
            className="w-full bg-green-500 text-white py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Завершить — деньги отправлены
          </button>
          {canRetryAuto && (
            <button
              type="button"
              disabled={busy}
              onClick={() => retry()}
              className="w-full bg-white/5 text-foreground py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Повторить авто-выплату
            </button>
          )}
        </div>
      )}
    </motion.div>
  );
}

function PayAmount({ data, onCopy }: { data: PayoutDetail; onCopy: (text: string) => void }) {
  // Ответ из кэша браузера, сохранённый до выкатки, этих полей не несёт.
  if (data.net_usd_minor == null) return null;
  const inRub = data.send_in_display;
  const net = inRub
    ? String(Math.round(data.net_display_minor / 100))
    : (data.net_usd_minor / 100).toFixed(2);
  const sign = data.display_currency === 'RUB' ? '₽' : data.display_currency;
  const shown = inRub ? `${Number(net).toLocaleString('ru-RU')} ${sign}` : `$${net}`;
  const rate = Number(data.display_rate).toLocaleString('ru-RU', { maximumFractionDigits: 2 });
  const parts = inRub ? [`$${(data.net_usd_minor / 100).toFixed(2)}`] : [];
  if (data.fee_usd_minor > 0) parts.push(`комиссия $${(data.fee_usd_minor / 100).toFixed(2)}`);
  if (inRub) parts.push(`курс ${rate} ${sign} за $`);
  return (
    <Card>
      <CardContent className="p-5">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {data.status === 'completed' ? 'Отправлено' : 'К отправке'}
        </div>
        <button
          type="button"
          onClick={() => onCopy(net)}
          className="mt-1 inline-flex items-center gap-2 text-2xl font-bold tabular-nums min-h-11"
        >
          {shown}
          <Copy className="w-4 h-4 shrink-0 opacity-60" />
        </button>
        <div className="text-xs text-muted-foreground">{parts.join(' · ')}</div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cfg: Record<string, string> = {
    pending: 'bg-yellow-500/15 text-yellow-500',
    approved: 'bg-blue-500/15 text-blue-400',
    completed: 'bg-green-500/15 text-green-500',
    rejected: 'bg-red-500/15 text-red-500',
  };
  const label: Record<string, string> = {
    pending: 'ожидает',
    approved: 'одобрено',
    completed: 'выплачено',
    rejected: 'отклонено',
  };
  const cls = cfg[status] ?? 'bg-white/10 text-muted-foreground';
  return (
    <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${cls}`}>
      {label[status] ?? status}
    </span>
  );
}

function Row({
  icon,
  label,
  value,
  children,
}: {
  icon?: React.ReactNode;
  label: string;
  value?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      {icon && <div className="text-primary mt-1">{icon}</div>}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div className="text-sm">{children || value || '—'}</div>
      </div>
    </div>
  );
}

function TimelineRow({ label, ts }: { label: string; ts: string }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{new Date(ts).toLocaleString('ru')}</span>
    </div>
  );
}
